import timingStore, { RecordStatus } from "@/store/timing.store";
import { formatSeconds2HMS } from "@/utils/formatter";
import { useEffect, useRef, useState } from "react";

// 修改图片预加载的方式
const getFrameUrl = (index: number) => {
  return new URL(`/src/assets/timeFrame/frame_${index}.png`, import.meta.url).href;
};

function RecordingAnimation() {
  const [currentFrame, setCurrentFrame] = useState(0);
  const status = timingStore((state) => state.recordStatus);
  const frameInterval = useRef<number>();
  const isPageVisible = useRef(true);
  const ref = useRef<HTMLImageElement>(null);

  // 添加图片对象缓存
  const imgCache = useRef<{ [key: number]: HTMLImageElement }>({});

  // 预加载下一帧
  const preloadNextFrame = (frameIndex: number) => {
    const nextIndex = frameIndex >= 23 ? 0 : frameIndex + 1;
    if (!imgCache.current[nextIndex]) {
      const img = new Image();
      img.src = getFrameUrl(nextIndex);
      imgCache.current[nextIndex] = img;
    }
  };

  // 清理不需要的缓存
  const cleanupCache = (currentIndex: number) => {
    Object.keys(imgCache.current).forEach(key => {
      const index = parseInt(key);
      if (index !== currentIndex && index !== (currentIndex + 1) % 24) {
        delete imgCache.current[index];
      }
    });
  };

  useEffect(() => {
    if (status === RecordStatus.RECORDING) {
      // 只预加载当前帧和下一帧
      preloadNextFrame(currentFrame);
    }

    // 定期清理缓存
    const cleanup = setInterval(() => {
      cleanupCache(currentFrame);
    }, 5000);

    return () => {
      clearInterval(cleanup);
    };
  }, [currentFrame, status]);

  // 添加页面可见性检测
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisible.current = document.visibilityState === 'visible';

      // 如果页面不可见，清除动画
      if (!isPageVisible.current && frameInterval.current) {
        clearInterval(frameInterval.current);
        frameInterval.current = undefined;
      } else if (isPageVisible.current && status === RecordStatus.RECORDING) {
        // 如果页面重新可见且正在录音，重新启动动画
        startAnimation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status]);

  const startAnimation = () => {
    if (frameInterval.current) {
      clearInterval(frameInterval.current);
    }

    frameInterval.current = window.setInterval(() => {
      if (!isPageVisible.current) return;

      setCurrentFrame((prev) => {
        if (prev >= 23) return 0;
        return prev + 1;
      });
    }, 1000 / 24);
  };

  useEffect(() => {
    const isRecording = status === RecordStatus.RECORDING;

    if (isRecording && isPageVisible.current) {
      startAnimation();
    } else {
      if (frameInterval.current) {
        clearInterval(frameInterval.current);
      }
      setCurrentFrame(0);
    }

    return () => {
      if (frameInterval.current) {
        clearInterval(frameInterval.current);
      }
    };
  }, [status]);

  return (
    <div className="relative w-[calc(100%-6rem)] h-12 flex justify-center mt-2">
      <img
        ref={ref}
        src={getFrameUrl(currentFrame)}
        className="w-150 h-10"
        style={{
          imageRendering: 'auto',  // 使用浏览器最佳图片渲染
          WebkitFontSmoothing: 'antialiased',
          filter: 'brightness(1.02)',  // 轻微提高亮度以减少锯齿感
        }}
        alt={`frame_${currentFrame}`}
      />
    </div>
  );
}

function RecordTime() {
  const recordTime = timingStore((state) => state.recordTime);

  return (
    <div className="flex-1 flex justify-center items-center m-l-3 m-r-4">
      <div className="text-3.5 text-mx-secondary p-r-4">
        <span>{formatSeconds2HMS(recordTime)}</span>
      </div>
      <RecordingAnimation />
    </div>
  );
}

export default RecordTime;
